/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import {
  useLocalization,
  PublicBreadcrumb,
  useDataProvider,
  SnackbarApiErrorMessage,
  LoadingEvent,
  Page,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { PageSection, Logo, IconLibrary } from '@product-framework/index';
import { useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { LOGO_TYPE } from '@product-framework/components/Logo';
import Viewers, { FILE_TYPE } from '@product-framework/components/Viewers';

function PublicHelpDetail() {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();
  const { formatMessage } = useLocalization();

  const initialBreadCrumb = [
    {
      name: formatMessage({
        id: 'LIST_PUBLIC',
        defaultMessage: 'Home',
      }),
      icon: IconLibrary.Home,
      onClick: () => navigate('/'),
    },
    {
      name: formatMessage({
        id: 'LIST_HELP',
        defaultMessage: 'Help',
      }),
      icon: IconLibrary.Help,
      onClick: () => {
        navigate('/public/help');
      },
    },
  ];

  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState(null);
  const [showFile, setShowFile] = useState(null);
  const [breadCrumb, setBreadCrumb] = useState(initialBreadCrumb);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get } = useDataProvider(provider);

  const loginAction = () => {
    window.location.href = '/';
  };

  const getHelpDocument = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/help-document/public/${pageId}`,
    );
    if (response.ok) {
      return response;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const initialize = async () => {
    setLoading(true);
    const response = await getHelpDocument();
    if (response) {
      setDocument(response?.data);
      if (response?.data?.video) {
        setShowFile(FILE_TYPE.VIDEO);
      } else if (response?.data?.document) {
        setShowFile(FILE_TYPE.PDF);
      }
    } else {
      setDocument(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (document?.title) {
      const newBreadCrumb = [
        ...initialBreadCrumb,
        {
          name: document?.title,
          icon: IconLibrary.Help,
          onClick: () => undefined,
        },
      ];
      setBreadCrumb(newBreadCrumb);
    }
  }, [document?.title]);

  return (
    <Page>
      <Container>
        <Grid2 container>
          <Grid2 xs={6} sx={{ mt: 1 }}>
            <Logo type={LOGO_TYPE.FULL} size="medium" href="/" />
          </Grid2>
          <Grid2 xs={6} sx={{ mt: 1, textAlign: 'right' }}>
            <Button variant="contained" size="small" onClick={loginAction}>
              <FormattedMessage id="LOGIN_BUTTON" defaultMessage="Login" />
            </Button>
          </Grid2>
          <Grid2 xs={12}>
            <Box sx={{ mb: 1 }}>
              <PublicBreadcrumb items={breadCrumb} />
            </Box>
            {loading ? (
              <LoadingEvent>Fetching document</LoadingEvent>
            ) : document ? (
              <PageSection
                containerProps={{ sx: { mb: 3 } }}
                label={formatMessage({
                  id: 'HELP_HEADING',
                  defaultMessage: document?.title || 'Help Document',
                })}>
                <Grid2>
                  {showFile && (
                    <Box sx={{ mb: 2 }}>
                      {document.video && document.document && (
                        <Stack spacing={2} direction="row">
                          {showFile === FILE_TYPE.PDF && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                setShowFile(FILE_TYPE.VIDEO);
                              }}
                              startIcon={<OndemandVideoIcon />}>
                              Watch Video
                            </Button>
                          )}
                          {showFile === FILE_TYPE.VIDEO && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setShowFile(FILE_TYPE.PDF);
                              }}
                              disabled={showFile === FILE_TYPE.PDF}
                              startIcon={<PictureAsPdfIcon />}>
                              View PDF
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Box>
                  )}
                  {showFile === FILE_TYPE.PDF && (
                    <Box>
                      <Viewers
                        file={document?.document}
                        fileType={FILE_TYPE.PDF}
                      />
                    </Box>
                  )}
                  {showFile === FILE_TYPE.VIDEO && (
                    <Box>
                      <Viewers
                        file={document?.video}
                        fileType={FILE_TYPE.VIDEO}
                      />
                    </Box>
                  )}
                </Grid2>
                <Grid2 sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {document.description}
                  </Typography>
                </Grid2>
                <Grid2 container sx={{ my: 2 }}>
                  <Grid2 xs>
                    <Stack direction="row" spacing={1}>
                      {(document.tags || []).map(t => (
                        <Chip key={t} sx={{ ml: 1 }} size="small" label={t} />
                      ))}
                    </Stack>
                  </Grid2>
                  {document?.version && (
                    <Grid2 xs>
                      <Stack alignItems="flex-end">
                        <Chip
                          color="primary"
                          size="small"
                          label={`${document.version}`}
                        />
                      </Stack>
                    </Grid2>
                  )}
                </Grid2>
              </PageSection>
            ) : (
              <PageSection
                containerProps={{ sx: { mb: 3 } }}
                label={formatMessage({
                  id: 'ERROR_HEADING',
                  defaultMessage: 'Not Found',
                })}>
                <Alert severity="error">
                  The resource you are looking for was not found
                </Alert>
              </PageSection>
            )}
          </Grid2>
        </Grid2>
      </Container>
    </Page>
  );
}
export default PublicHelpDetail;
